import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CardComp, ButtonComp } from "../../../components";
import PageWrapper from "../modules/PageWrapper";
import { formatMoney } from "../../../utils";
import toast from "react-hot-toast";
import {
  useVerifyPaymentStatusPublicQuery,
  useManuallyVerifyPaymentPublicMutation,
} from "../../../store/member/member-contribution";
import { useCompleteSignupAfterPaymentMutation } from "../../../store/auth/authApi";
import axios from "axios";
import { paymentConfirmationUrl } from "../../../utils/paymentConfirmationRoutes";

const AWAITING_CONFIRMATION_MESSAGE =
  "We have not confirmed this payment yet. If you completed payment, it can take a few minutes—check your history shortly. If you closed or cancelled the payment window, nothing was charged and you can try again.";

export default function PaymentConfirmation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState("processing"); // processing | awaiting | success | failed
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [isVerifying, setIsVerifying] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3; // Reduced to 3 retries to avoid rate limiting
  const RETRY_DELAY = 5000; // Increased to 5 seconds between retries
  const hasManuallyVerified = useRef(false); // Prevent multiple manual verification calls
  const retryTimeoutRef = useRef(null); // Store timeout reference to clear if needed
  const hasAttemptedSignup = useRef(false); // Prevent multiple signup completion attempts
  const hasLeftProcessing = useRef(false); // Once awaiting/failed, don't auto-spin again

  // Investment / marketplace confirmations have their own pages — redirect if we landed here.
  useEffect(() => {
    const ref = searchParams.get("reference") || searchParams.get("trxref");
    if (!ref) return;
    if (
      String(ref).startsWith("PAYMENT_INVESTMENT_") ||
      String(ref).startsWith("PAYMENT_MARKETPLACE_")
    ) {
      navigate(paymentConfirmationUrl(ref), { replace: true });
    }
  }, [searchParams, navigate]);

  const resolvePaymentTypeLabel = useCallback((payment = {}, reference = null) => {
    const ref = reference;
    const isLoanPayment =
      payment.payment_type === "loan_repayment" ||
      (ref && String(ref).startsWith("PAYMENT_LOAN_"));
    const isAttendanceFinePayment =
      payment.payment_type === "ATTENDANCE_FINE" ||
      payment.payment_type === "attendance_fine" ||
      (ref && String(ref).startsWith("PAYMENT_ATT_FINE_"));
    const isSignupPaymentType =
      payment.payment_type === "MEMBER_SIGNUP" ||
      payment.payment_type === "REGISTRATION" ||
      payment.payment_type === "signup" ||
      (ref &&
        (String(ref).startsWith("PAYMENT_SIGNUP_") ||
          String(ref).startsWith("PAYMENT_REGISTRATION_") ||
          String(ref).startsWith("PAYMENT_SIGN")));
    const isInvestmentPayment =
      payment.payment_type === "investment" ||
      (ref && String(ref).startsWith("PAYMENT_INVESTMENT_"));
    const isMarketplacePayment =
      payment.payment_type === "marketplace" ||
      (ref && String(ref).startsWith("PAYMENT_MARKETPLACE_"));

    if (isLoanPayment) return "loan_repayment";
    if (isAttendanceFinePayment) return "attendance_fine";
    if (isSignupPaymentType) return "signup";
    if (isInvestmentPayment) return "investment";
    if (isMarketplacePayment) return "marketplace";
    return "contribution";
  }, []);

  const enterAwaitingConfirmation = useCallback(
    (payment = {}, reference = null) => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      hasLeftProcessing.current = true;
      setPaymentStatus("awaiting");
      setIsVerifying(false);
      setPaymentDetails({
        reference: payment.payment_reference || payment.reference || reference,
        amount: payment.total_amount ?? payment.amount,
        payment_provider: payment.payment_provider,
        payment_type: resolvePaymentTypeLabel(payment, reference),
        message: payment.message || AWAITING_CONFIRMATION_MESSAGE,
        showManualCheck: true,
      });
    },
    [resolvePaymentTypeLabel]
  );  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);
  
  // Debug logging for signup payments
  useEffect(() => {
    const ref = searchParams.get("reference") || searchParams.get("trxref");
    console.log("PaymentConfirmation mounted", {
      paymentStatus,
      isVerifying,
      paymentReference: ref,
      isSignupPayment: ref && (ref.startsWith('PAYMENT_SIGNUP_') || ref.startsWith('PAYMENT_REGISTRATION_')),
      signupData: sessionStorage.getItem("pending_signup_data") ? "exists" : "missing",
      retryCount,
    });
  }, [searchParams, paymentStatus, isVerifying, retryCount]);

  // Get payment reference from URL params (Paystack returns 'reference' or 'trxref')
  // Also check sessionStorage as fallback in case Paystack doesn't redirect properly
  const urlReference =
    searchParams.get("reference") || searchParams.get("trxref");
  const storedReference = sessionStorage.getItem("pending_payment_reference");
  const paymentReference = urlReference || storedReference;
  const alatpayTransactionId =
    searchParams.get("alatpay_transaction_id") ||
    searchParams.get("transaction_id") ||
    sessionStorage.getItem("alatpay_transaction_id") ||
    null;

  // Persist ALATPay txn id from URL so manual verify / refresh still works
  useEffect(() => {
    if (alatpayTransactionId) {
      sessionStorage.setItem("alatpay_transaction_id", alatpayTransactionId);
    }
  }, [alatpayTransactionId]);

  // Attach ALATPay UUID early so status/manual verify can call Get-Single
  useEffect(() => {
    if (!paymentReference || !alatpayTransactionId) return undefined;
    let cancelled = false;
    const attach = async () => {
      try {
        await axios.post(
          `${process.env.REACT_APP_BASE_URL}/public/payments/alatpay-attach-transaction`,
          {
            reference: paymentReference,
            transaction_id: String(alatpayTransactionId),
            verify: false,
          }
        );
      } catch (_err) {
        // Non-blocking — manual verify will retry with the same id
      }
      if (!cancelled) {
        // no-op; status poll / manual verify picks up after attach
      }
    };
    attach();
    return () => {
      cancelled = true;
    };
  }, [paymentReference, alatpayTransactionId]);

  // If we have stored reference but no URL param, update URL for better UX
  useEffect(() => {
    if (storedReference && !urlReference) {
      const href = paymentConfirmationUrl(storedReference, null, {
        alatpay_transaction_id: alatpayTransactionId || undefined,
      });
      navigate(href, { replace: true });
    }
  }, [storedReference, urlReference, navigate, alatpayTransactionId]);

  // Clear stored reference once we have it from URL
  useEffect(() => {
    if (urlReference && storedReference) {
      sessionStorage.removeItem("pending_payment_reference");
    }
  }, [urlReference, storedReference]);

  // Use RTK Query to verify payment status (public endpoint - no auth required)
  // Disable automatic polling - we'll handle retries manually with delays to avoid rate limiting
  const {
    data: paymentData,
    error: paymentError,
    isLoading: isVerifyingPayment,
    refetch: refetchPayment,
  } = useVerifyPaymentStatusPublicQuery(
    { reference: paymentReference },
    { 
      skip: !paymentReference,
      pollingInterval: 0, // Disable automatic polling
    }
  );

  // Manual verification mutation (provider-aware: Paystack or ALATPay/Wema) - public endpoint
  const [manuallyVerifyPayment, { isLoading: isManuallyVerifying }] =
    useManuallyVerifyPaymentPublicMutation();
  
  // Complete signup after payment mutation
  const [completeSignup, { isLoading: isCompletingSignup }] = useCompleteSignupAfterPaymentMutation();
  
  // Check if this is a signup payment
  // Handle various reference formats: PAYMENT_SIGNUP_, PAYMENT_REGISTRATION_, PAYMENT_SIGN, etc.
  const isSignupPayment = paymentReference && (
    paymentReference.startsWith('PAYMENT_SIGNUP_') || 
    paymentReference.startsWith('PAYMENT_REGISTRATION_') ||
    paymentReference.startsWith('PAYMENT_SIGN') // Handle truncated references like PAYMENT_SIGN
  );
  
  // Get stored signup data
  const [signupData, setSignupData] = useState(null);
  
  // Handle completing signup after successful payment
  const handleCompleteSignup = useCallback(async (memberData, paymentReference) => {
    // Prevent multiple attempts
    if (hasAttemptedSignup.current) {
      console.log("Signup already attempted, skipping...");
      return;
    }
    
    hasAttemptedSignup.current = true; // Mark as attempted immediately
    
    try {
      setPaymentStatus("processing");
      setPaymentDetails({
        message: "Payment successful! Completing your registration...",
      });
      
      // Call signup endpoint with payment reference using mutation
      const response = await completeSignup({
        ...memberData,
        paystack_transaction_reference: paymentReference,
      }).unwrap();
      
      if (!response || response.status === false) {
        // Clear signup data after failed attempt to prevent retries
        sessionStorage.removeItem("pending_signup_data");
        sessionStorage.removeItem("pending_signup_payment_reference");
        setSignupData(null); // Clear signup data from state
        
        setPaymentStatus("failed");
        setPaymentDetails({
          message: response?.message || "Registration failed. Please contact support.",
          showManualCheck: false,
          payment_type: 'signup', // Set payment type for correct button text
        });
        return;
      }
      
      // Registration successful - clear stored data
      sessionStorage.removeItem("pending_signup_data");
      sessionStorage.removeItem("pending_signup_payment_reference");
      try {
        sessionStorage.removeItem("pending_signup_referred_by_member_no");
      } catch {
        /* ignore */
      }
      setSignupData(null); // Clear signup data from state
      
      setPaymentStatus("success");
      setPaymentDetails({
        reference: paymentReference,
        amount: memberData.amount,
        payment_type: 'signup',
        message: "Registration successful! An email has been sent to the admin. You will be notified once your account is approved.",
      });
      
      toast.success("Registration completed successfully!");
    } catch (error) {
      console.error("Signup completion error:", error);
      
      // Clear signup data after failed attempt to prevent retries
      sessionStorage.removeItem("pending_signup_data");
      sessionStorage.removeItem("pending_signup_payment_reference");
      setSignupData(null); // Clear signup data from state
      
      setPaymentStatus("failed");
      setPaymentDetails({
        message: error.response?.data?.message || "Failed to complete registration. Please contact support.",
        showManualCheck: false,
        payment_type: 'signup', // Set payment type for correct button text
      });
      toast.error("Registration failed. Please contact support.");
    }
  }, [completeSignup]);
  
  useEffect(() => {
    if (isSignupPayment) {
      const storedData = sessionStorage.getItem("pending_signup_data");
      if (storedData) {
        try {
          setSignupData(JSON.parse(storedData));
        } catch (e) {
          console.error("Failed to parse signup data:", e);
        }
      }
    }
  }, [isSignupPayment]);

  useEffect(() => {
    if (!paymentReference) {
      setPaymentStatus("failed");
      setPaymentDetails({
        message:
          "Payment reference not found. Please contact support if payment was deducted.",
      });
      setIsVerifying(false);
      return;
    }
  }, [paymentReference]);

  useEffect(() => {
    if (isVerifyingPayment) {
      setIsVerifying(true);
      return;
    }

    if (paymentError) {
      // Handle rate limiting (429) gracefully
      if (paymentError?.status === 429 || paymentError?.data?.message?.includes("Too Many Attempts")) {
        enterAwaitingConfirmation(
          {
            message:
              "Verification is being rate-limited. Please wait a moment, then try Verify Payment or leave and check your history later.",
          },
          paymentReference
        );
        return;
      }

      const rawMessage =
        paymentError?.data?.message ||
        paymentError?.message ||
        "";
      const isSslOrConnectionError =
        /cURL error 56|SSL|OpenSSL|sslv3 alert|SSL_read|errno 0/i.test(rawMessage);
      const isLoanRef =
        paymentReference && String(paymentReference).startsWith("PAYMENT_LOAN_");

      hasLeftProcessing.current = true;
      setPaymentStatus("failed");
      setPaymentDetails({
        message: isSslOrConnectionError
          ? "We couldn't verify your payment right now due to a temporary connection issue with our payment provider. Your payment may still have been successful. Please try \"Verify Payment\" in a moment, or check your loan/contribution balance. If the problem continues, contact support."
          : rawMessage || "Unable to verify payment status. Please contact support.",
        showManualCheck: true,
        payment_type: isLoanRef ? "loan_repayment" : undefined,
      });
      setIsVerifying(false);

      if (!isSslOrConnectionError) {
        console.error("Payment verification error:", paymentError);
      }
      return;
    }

    if (paymentData) {
      const payment = paymentData;

      // Determine payment type
      const isLoanPayment = payment.payment_type === 'loan_repayment' || 
                           (payment.payment_reference && payment.payment_reference.startsWith('PAYMENT_LOAN_'));
      const isAttendanceFinePayment = payment.payment_type === 'ATTENDANCE_FINE' || 
                                     (payment.payment_reference && payment.payment_reference.startsWith('PAYMENT_ATT_FINE_'));
      const isSignupPaymentType = payment.payment_type === 'MEMBER_SIGNUP' || 
                                 payment.payment_type === 'REGISTRATION' ||
                                 (payment.payment_reference && (
                                   payment.payment_reference.startsWith('PAYMENT_SIGNUP_') ||
                                   payment.payment_reference.startsWith('PAYMENT_REGISTRATION_') ||
                                   payment.payment_reference.startsWith('PAYMENT_SIGN')
                                 ));

      if (payment.status === "SUCCESSFUL" || payment.status === "PROCESSED") {
        // Reset retry count and manual verification flag on success
        setRetryCount(0);
        hasManuallyVerified.current = false;
        
        // If this is a signup payment and we have signup data, complete the registration
        // Only attempt if we haven't already tried (prevents infinite loop)
        if (isSignupPaymentType && signupData && !isCompletingSignup && !hasAttemptedSignup.current) {
          handleCompleteSignup(signupData, payment.payment_reference);
          return; // Don't set status yet - wait for signup completion
        }
        
        setPaymentStatus("success");
        
        let paymentTypeLabel = 'contribution';
        if (isLoanPayment) paymentTypeLabel = 'loan_repayment';
        else if (isAttendanceFinePayment) paymentTypeLabel = 'attendance_fine';
        else if (isSignupPaymentType) paymentTypeLabel = 'signup';

        setPaymentDetails({
          reference: payment.payment_reference,
          amount: payment.total_amount,
          contributions_count: payment.contributions_count || 1,
          payment_type: paymentTypeLabel,
          message: isLoanPayment 
            ? "Your loan repayment was processed successfully!" 
            : isAttendanceFinePayment
            ? "Your attendance fine payment was processed successfully!"
            : isSignupPaymentType
            ? "Payment successful! Completing your registration..."
            : "Your payment was processed successfully!",
        });

        // Auto-redirect after 5 seconds (only if user is logged in, otherwise show message)
        // For mobile users, they'll need to login to see their payments
        // For signup payments, don't auto-redirect - show success message
        if (!isSignupPaymentType) {
          setTimeout(() => {
            // Check if user is logged in by trying to navigate
            // If not logged in, stay on page with success message
            const token =
              localStorage.getItem("access_token") ||
              document.cookie.includes("access_token");
            if (token) {
              // Redirect based on payment type
              if (isLoanPayment) {
                navigate("/member/loan_management/loan_repayment");
              } else if (isAttendanceFinePayment) {
                navigate("/member/attendance");
              } else {
                navigate("/member/contribution");
              }
            }
          }, 5000);
        }
      } else if (payment.status === "PENDING") {
        // User already moved to awaiting/leave UI — don't restart the spinner loop
        if (hasLeftProcessing.current) {
          setIsVerifying(false);
          return;
        }

        // Payment is still pending - webhook might be processing
        // For signup payments, show appropriate message and handle retries
        if (isSignupPaymentType) {
          if (retryCount >= MAX_RETRIES) {
            enterAwaitingConfirmation(
              {
                ...payment,
                message: AWAITING_CONFIRMATION_MESSAGE,
              },
              paymentReference
            );
            return;
          }
          
          setPaymentStatus("processing");
          setPaymentDetails({
            message: `Your payment is being processed. We'll complete your registration once payment is confirmed... (${
              retryCount + 1
            }/${MAX_RETRIES})`,
            showManualCheck: retryCount >= 1, // Show manual verify button after 1 retry
            payment_type: 'signup', // Set payment type for correct button text
          });
          
          // Clear any existing timeout
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
          }
          
          // Retry verification after delay (exponential backoff: 5s, 10s, 15s)
          // Use current retryCount BEFORE incrementing for delay calculation
          const delay = RETRY_DELAY * (retryCount + 1); // 5s, 10s, 15s
          retryTimeoutRef.current = setTimeout(() => {
            refetchPayment();
          }, delay);
          
          // Increment retry count AFTER setting timeout
          setRetryCount((prev) => prev + 1);
          setIsVerifying(false);
          return;
        }
        
        // After retries, do one provider verify — then exit spinner so the user is not stuck
        if (retryCount >= MAX_RETRIES) {
          if (
            !hasManuallyVerified.current &&
            paymentReference &&
            !isManuallyVerifying
          ) {
            hasManuallyVerified.current = true;
            setPaymentStatus("processing");
            setPaymentDetails({
              message: "Checking with your payment provider...",
              showManualCheck: false,
            });

            manuallyVerifyPayment({
              reference: paymentReference,
              transaction_id: alatpayTransactionId || undefined,
            })
              .unwrap()
              .then((result) => {
                if (
                  result &&
                  (result.status === "SUCCESSFUL" || result.status === "PROCESSED")
                ) {
                  setTimeout(() => refetchPayment(), 1000);
                  return;
                }
                enterAwaitingConfirmation(
                  {
                    ...(result || payment),
                    message: AWAITING_CONFIRMATION_MESSAGE,
                  },
                  paymentReference
                );
              })
              .catch(() => {
                enterAwaitingConfirmation(payment, paymentReference);
              });
          } else {
            enterAwaitingConfirmation(payment, paymentReference);
          }
          setIsVerifying(false);
          return;
        }

        setPaymentStatus("processing");
        setPaymentDetails({
          message: `Your payment is being processed. Please wait... (${
            retryCount + 1
          }/${MAX_RETRIES})`,
          showManualCheck: retryCount >= 1,
        });

        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }

        const delay = RETRY_DELAY * (retryCount + 1);
        retryTimeoutRef.current = setTimeout(() => {
          refetchPayment();
        }, delay);

        setRetryCount((prev) => prev + 1);
      } else {
        // Payment status is neither SUCCESSFUL, PROCESSED, nor PENDING (could be FAILED)
        // For FAILED status, still allow manual verification to retry if the gateway confirms success
        const isLoanPayment = payment.payment_type === 'loan_repayment' || 
                             (payment.payment_reference && payment.payment_reference.startsWith('PAYMENT_LOAN_'));
        const isAttendanceFinePayment = payment.payment_type === 'ATTENDANCE_FINE' || 
                                       (payment.payment_reference && payment.payment_reference.startsWith('PAYMENT_ATT_FINE_'));
        
        if (payment.status === "FAILED") {
          setPaymentStatus("failed");
          setPaymentDetails({
            message: `Payment status: ${payment.status}. Please verify payment if the amount was deducted.`,
            showManualCheck: true, // Allow manual verification for FAILED payments
            payment_type: isLoanPayment ? 'loan_repayment' : isAttendanceFinePayment ? 'attendance_fine' : 'contribution',
          });
        } else {
          setPaymentStatus("failed");
          setPaymentDetails({
            message: `Payment status: ${payment.status}. Please contact support if payment was deducted.`,
            showManualCheck: true,
            payment_type: isLoanPayment ? 'loan_repayment' : isAttendanceFinePayment ? 'attendance_fine' : 'contribution',
          });
        }
      }
      setIsVerifying(false);
    }
  }, [
    paymentData,
    paymentError,
    isVerifyingPayment,
    refetchPayment,
    navigate,
    retryCount,
    paymentReference,
    isManuallyVerifying,
    manuallyVerifyPayment,
    signupData,
    isCompletingSignup,
    handleCompleteSignup,
    enterAwaitingConfirmation,
    alatpayTransactionId,
  ]);

  const handleGoToContributions = () => {
    // Check if this is a signup payment
    const isSignupPaymentType = paymentDetails?.payment_type === 'signup' ||
                               (paymentReference && (
                                 paymentReference.startsWith('PAYMENT_SIGNUP_') ||
                                 paymentReference.startsWith('PAYMENT_REGISTRATION_') ||
                                 paymentReference.startsWith('PAYMENT_SIGN')
                               ));
    
    // For signup payments, redirect to login page (member needs to login after admin approval)
    if (isSignupPaymentType) {
      navigate("/login", { 
        state: { 
          message: "Your registration is pending admin approval. Please log in after your account is approved." 
        } 
      });
      return;
    }
    
    // Check if user is logged in
    const token =
      localStorage.getItem("access_token") ||
      document.cookie.includes("access_token");
    
    // Determine payment type to redirect appropriately
    const isLoanPayment = paymentDetails?.payment_type === 'loan_repayment' ||
                         (paymentReference && paymentReference.startsWith('PAYMENT_LOAN_'));
    const isAttendanceFinePayment = paymentDetails?.payment_type === 'attendance_fine' ||
                                   (paymentReference && paymentReference.startsWith('PAYMENT_ATT_FINE_'));
    const isInvestmentPayment = paymentDetails?.payment_type === 'investment' ||
                               (paymentReference && paymentReference.startsWith('PAYMENT_INVESTMENT_'));
    const isMarketplacePayment = paymentDetails?.payment_type === 'marketplace' ||
                                (paymentReference && paymentReference.startsWith('PAYMENT_MARKETPLACE_'));
    
    if (token) {
      // Redirect based on payment type
      if (isLoanPayment) {
        navigate("/member/loan_management/loan_repayment");
      } else if (isAttendanceFinePayment) {
        navigate("/member/attendance");
      } else if (isInvestmentPayment) {
        navigate("/invest/requests?filter=investment");
      } else if (isMarketplacePayment) {
        navigate("/invest/requests?filter=marketplace");
      } else {
        navigate("/member/contribution");
      }
    } else {
      // Redirect to login, then to appropriate page
      let redirectTo = "/member/contribution";
      if (isLoanPayment) redirectTo = "/member/loan_management/loan_repayment";
      else if (isAttendanceFinePayment) redirectTo = "/member/attendance";
      else if (isInvestmentPayment) redirectTo = "/invest/requests?filter=investment";
      else if (isMarketplacePayment) redirectTo = "/invest/requests?filter=marketplace";
      
      navigate("/login", { state: { redirectTo } });
    }
  };

  const handleRetry = () => {
    if (!paymentReference) return;
    hasLeftProcessing.current = false;
    hasManuallyVerified.current = false;
    setRetryCount(0);
    setPaymentStatus("processing");
    setIsVerifying(true);
    setPaymentDetails({
      message: "Checking payment status...",
      showManualCheck: false,
    });
    refetchPayment();
  };

  const handleManualVerify = () => {
    if (!paymentReference || isManuallyVerifying) return;

    hasManuallyVerified.current = true;
    setPaymentStatus("processing");
    setIsVerifying(true);
    setPaymentDetails({
      message: "Verifying your payment...",
      showManualCheck: false,
    });

    manuallyVerifyPayment({
      reference: paymentReference,
      transaction_id: alatpayTransactionId || undefined,
    })
      .unwrap()
      .then((result) => {
        // Check the result directly - it should have the updated status
        if (result && (result.status === "SUCCESSFUL" || result.status === "PROCESSED")) {
          // Check if this is a signup payment
          const isSignupPaymentType = result.payment_type === 'MEMBER_SIGNUP' ||
                                     (paymentReference && (
                                       paymentReference.startsWith('PAYMENT_SIGNUP_') ||
                                       paymentReference.startsWith('PAYMENT_REGISTRATION_') ||
                                       paymentReference.startsWith('PAYMENT_SIGN')
                                     ));
          
          // If this is a signup payment and we have signup data, complete the registration
          // Only attempt if we haven't already tried (prevents infinite loop)
          if (isSignupPaymentType && signupData && !isCompletingSignup && !hasAttemptedSignup.current) {
            handleCompleteSignup(signupData, paymentReference);
            return; // Don't set status yet - wait for signup completion
          }
          
          // Payment was successfully processed
          const isLoanPayment = result.payment_type === 'loan_repayment' || 
                               (paymentReference && paymentReference.startsWith('PAYMENT_LOAN_'));
          const isAttendanceFinePayment = result.payment_type === 'ATTENDANCE_FINE' || 
                                         (paymentReference && paymentReference.startsWith('PAYMENT_ATT_FINE_'));
          
          setPaymentStatus("success");
          setPaymentDetails({
            reference: result.payment_reference || paymentReference,
            amount: result.total_amount,
            contributions_count: result.contributions_count || 1,
            payment_type: isLoanPayment ? 'loan_repayment' : isAttendanceFinePayment ? 'attendance_fine' : isSignupPaymentType ? 'signup' : 'contribution',
            message: isLoanPayment 
              ? "Your loan repayment was processed successfully!" 
              : isAttendanceFinePayment
              ? "Your attendance fine payment was processed successfully!"
              : isSignupPaymentType
              ? "Payment successful! Completing your registration..."
              : "Your payment was processed successfully!",
          });
          setIsVerifying(false);
          sessionStorage.removeItem("alatpay_transaction_id");

          // Auto-redirect after 5 seconds if logged in (skip for signup payments)
          if (!isSignupPaymentType) {
            setTimeout(() => {
              const token =
                localStorage.getItem("access_token") ||
                document.cookie.includes("access_token");
              if (token) {
                if (isLoanPayment) {
                  navigate("/member/loan_management/loan_repayment");
                } else if (isAttendanceFinePayment) {
                  navigate("/member/attendance");
                } else {
                  navigate("/member/contribution");
                }
              }
            }, 5000);
          }
        } else {
          // Provider has not confirmed yet — leave processing so the user is not stuck
          enterAwaitingConfirmation(
            {
              ...(result || {}),
              message: AWAITING_CONFIRMATION_MESSAGE,
            },
            paymentReference
          );
        }
      })
      .catch((error) => {
        // Manual verification failed - show error
        hasLeftProcessing.current = true;
        setPaymentStatus("failed");
        setPaymentDetails({
          message:
            error?.data?.message ||
            error?.message ||
            "Unable to verify payment. Please contact support if payment was deducted.",
          showManualCheck: true,
        });
        setIsVerifying(false);
      });
  };

  const historyButtonText =
    isSignupPayment || paymentDetails?.payment_type === "signup"
      ? "Go to Login"
      : paymentDetails?.payment_type === "loan_repayment"
      ? "Go to Loan Repayments"
      : paymentDetails?.payment_type === "attendance_fine"
      ? "Go to Attendance"
      : paymentDetails?.payment_type === "investment"
      ? "Go to My Requests"
      : paymentDetails?.payment_type === "marketplace"
      ? "Go to Marketplace Orders"
      : "Go to Contributions";

  const tryAgainPath =
    paymentDetails?.payment_type === "loan_repayment"
      ? "/member/loan_management/loan_repayment"
      : paymentDetails?.payment_type === "attendance_fine"
      ? "/member/attendance"
      : paymentDetails?.payment_type === "investment"
      ? "/invest/investments"
      : paymentDetails?.payment_type === "marketplace"
      ? "/invest/marketplace"
      : "/member/contribution/record";

  const handleTryAgain = () => {
    const token =
      localStorage.getItem("access_token") ||
      document.cookie.includes("access_token");
    if (token) {
      navigate(tryAgainPath);
      return;
    }
    navigate("/login", { state: { redirectTo: tryAgainPath } });
  };

  // Ensure component always renders something
  if (!paymentReference && !isVerifying) {
    return (
      <PageWrapper title="Payment Confirmation" back={false}>
        <div className="px-3 px-lg-4 d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
          <CardComp cardClassName="border-0 pt-4 px-4 pb-5" style={{ maxWidth: "500px", width: "100%" }}>
            <div className="text-center py-5">
              <h5 className="fw-bold text-black1 mb-2">Payment Reference Missing</h5>
              <p className="text-muted mb-3">No payment reference found. Please contact support if payment was deducted.</p>
            </div>
          </CardComp>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper title="Payment Confirmation" back={false}>
      <div
        className="px-3 px-lg-4 d-flex justify-content-center align-items-center"
        style={{ minHeight: "60vh" }}
      >
        <CardComp
          cardClassName="border-0 pt-4 px-4 pb-5"
          style={{ maxWidth: "500px", width: "100%" }}
        >
          {isVerifying || paymentStatus === "processing" ? (
            <div className="text-center py-5">
              <div
                className="spinner-border text-primary1 mb-4"
                role="status"
                style={{ width: "4rem", height: "4rem" }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <h5 className="fw-bold text-black1 mb-2">Processing Payment</h5>
              <p className="text-muted mb-3">
                {paymentDetails?.message ||
                  "Please wait while we verify your payment..."}
              </p>
              {paymentDetails?.showManualCheck && (
                <div className="mt-4">
                  <div className="d-flex gap-3 justify-content-center flex-wrap">
                    <ButtonComp
                      btnText="Verify Payment"
                      btnClassName="bg-primary1 text-white rounded py-2 px-4"
                      onClick={handleManualVerify}
                      loading={isManuallyVerifying}
                    />
                    <ButtonComp
                      btnText={historyButtonText}
                      btnClassName="bg-white text-primary1 border border-primary1 rounded py-2 px-4"
                      onClick={handleGoToContributions}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : paymentStatus === "awaiting" ? (
            <div className="text-center py-4">
              <div className="mb-4">
                <div
                  className="rounded-circle d-inline-flex align-items-center justify-content-center"
                  style={{
                    width: "80px",
                    height: "80px",
                    backgroundColor: "rgba(255, 149, 0, 0.15)",
                    color: "#da6900",
                    fontSize: "36px",
                    fontWeight: 600,
                  }}
                >
                  !
                </div>
              </div>
              <h4 className="fw-bold text-black1 mb-3">Payment not confirmed yet</h4>
              <p className="text-muted mb-4">
                {paymentDetails?.message || AWAITING_CONFIRMATION_MESSAGE}
              </p>
              {(paymentDetails?.reference || paymentReference) && (
                <div className="bg-light rounded p-3 mb-4 text-start">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Reference:</span>
                    <span
                      className="fw-semibold text-black1"
                      style={{ fontFamily: "monospace", fontSize: "13px" }}
                    >
                      {paymentDetails?.reference || paymentReference}
                    </span>
                  </div>
                  {paymentDetails?.amount != null && (
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">Amount:</span>
                      <span className="fw-semibold text-black1">
                        {formatMoney(paymentDetails.amount)}
                      </span>
                    </div>
                  )}
                </div>
              )}
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <ButtonComp
                  btnText="Verify Payment"
                  btnClassName="bg-primary1 text-white rounded py-2 px-4"
                  onClick={handleManualVerify}
                  loading={isManuallyVerifying}
                />
                <ButtonComp
                  btnText="Refresh Status"
                  btnClassName="bg-secondary2 text-white rounded py-2 px-4"
                  onClick={handleRetry}
                />
                <ButtonComp
                  btnText={historyButtonText}
                  btnClassName="bg-white text-primary1 border border-primary1 rounded py-2 px-4"
                  onClick={handleGoToContributions}
                />
                {!isSignupPayment && paymentDetails?.payment_type !== "signup" && (
                  <ButtonComp
                    btnText="Try payment again"
                    btnClassName="bg-white text-secondary2 border border-secondary2 rounded py-2 px-4"
                    onClick={handleTryAgain}
                  />
                )}
              </div>
            </div>
          ) : paymentStatus === "success" ? (
            <div className="text-center py-4">
              <div className="mb-4">
                <div
                  className="rounded-circle bg-success d-inline-flex align-items-center justify-content-center"
                  style={{ width: "80px", height: "80px" }}
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="feather feather-check"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
              </div>
              <h4 className="fw-bold text-black1 mb-3">Payment Successful!</h4>
              {paymentDetails && (
                <>
                  <p className="text-muted mb-3">{paymentDetails.message}</p>
                  <div className="bg-light rounded p-3 mb-4 text-start">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Reference:</span>
                      <span className="fw-semibold text-black1" style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                        {paymentDetails.reference}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Amount:</span>
                      <span className="fw-semibold text-black1">
                        {formatMoney(paymentDetails.amount)}
                      </span>
                    </div>
                    {paymentDetails.contributions_count > 1 && paymentDetails.payment_type === 'contribution' && (
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Contributions:</span>
                        <span className="fw-semibold text-black1">
                          {paymentDetails.contributions_count} items
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="text-muted small mb-4">
                    {(() => {
                      const token =
                        localStorage.getItem("access_token") ||
                        document.cookie.includes("access_token");
                      
                      if (paymentDetails.payment_type === 'signup') {
                        return "Registration successful! An email has been sent to the admin. You will be notified once your account is approved.";
                      }
                      
                      let destination = "contribution history";
                      if (paymentDetails.payment_type === 'loan_repayment') destination = "loan repayments";
                      else if (paymentDetails.payment_type === 'attendance_fine') destination = "attendance dashboard";

                      return token
                        ? `Redirecting to ${destination} in a few seconds...`
                        : `Payment successful! Please log in to view your ${destination.replace(' history', '')}.`;
                    })()}
                  </p>
                </>
              )}
              <div className="d-flex flex-column gap-2">
                <ButtonComp
                  btnText={isSignupPayment || paymentDetails?.payment_type === 'signup'
                    ? "Go to Login"
                    : paymentDetails?.payment_type === 'loan_repayment' 
                    ? "View Loan Repayments" 
                    : paymentDetails?.payment_type === 'attendance_fine'
                    ? "View Attendance"
                    : "View Contributions"}
                  btnClassName="bg-primary1 text-white rounded py-2 px-4"
                  onClick={handleGoToContributions}
                />
                <p className="text-muted small mb-0 mt-2">
                  {paymentDetails?.payment_type === 'signup' 
                    ? "Your registration is pending admin approval. You'll be able to log in once approved."
                    : `Note: You may need to log in to view your ${paymentDetails?.payment_type === 'loan_repayment' ? 'loan repayments' : paymentDetails?.payment_type === 'attendance_fine' ? 'attendance' : 'contributions'}`}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="mb-4">
                <div
                  className="rounded-circle bg-danger d-inline-flex align-items-center justify-content-center"
                  style={{ width: "80px", height: "80px" }}
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="feather feather-x"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </div>
              </div>
              <h4 className="fw-bold text-black1 mb-3">Payment Failed</h4>
              {paymentDetails && (
                <>
                  <p className="text-danger mb-4">{paymentDetails.message}</p>
                  {paymentReference && (
                    <div className="bg-light rounded p-3 mb-4 text-start">
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Reference:</span>
                        <span className="fw-semibold text-black1" style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                          {paymentReference}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <ButtonComp
                  btnText="Verify Payment"
                  btnClassName="bg-primary1 text-white rounded py-2 px-4"
                  onClick={handleManualVerify}
                  loading={isManuallyVerifying}
                />
                <ButtonComp
                  btnText="Refresh Status"
                  btnClassName="bg-secondary2 text-white rounded py-2 px-4"
                  onClick={handleRetry}
                />
                <ButtonComp
                  btnText={historyButtonText}
                  btnClassName="bg-white text-primary1 border border-primary1 rounded py-2 px-4"
                  onClick={handleGoToContributions}
                />
              </div>
            </div>
          )}
        </CardComp>
      </div>
    </PageWrapper>
  );
}
